import { useApi } from '@src/app/context/Api';
import { dayjs } from '@uhg-abyss/web/tools/dayjs';
import { isEmpty, isEqual } from 'lodash';
import { useCallback, useMemo, useState } from 'react';

/**
 * useSearch
 *
 * Retrieves all the necessary data to populate the widgets on the Risk Dashboard.
 *
 * @returns {{data, handleSearch: function({}=): void, isSearching: boolean}}
 */
export const useSearch = () => {
  const { useApiQueries } = useApi();
  const [theQueries, setTheQueries] = useState([]);
  const queries = useApiQueries(theQueries);

  // Compute searching state directly from queries
  const isSearching = useMemo(() => {
    return (
      isEmpty(queries) ||
      Object.keys(queries).length !== theQueries.length ||
      !isEmpty(
        Object.keys(queries).filter((queryKey) => {
          const query = queries[queryKey];
          return query?.isLoading || query?.isFetching;
        })
      )
    );
  }, [queries, theQueries]);

  const handleSearch = useCallback(
    (payload = {}) => {
      const requests = [
        {
          apiKey: 'RiskRecord',
          payload: { date: dayjs(payload?.reportDate).format('YYYY-MM-DD') },
          queryKey: 'GetRiskDashboardStatistics',
        },
        {
          apiKey: 'Source',
          payload: { date: dayjs(payload?.reportDate).format('YYYY-MM-DD') },
          queryKey: 'GetUntrustedSourceCount',
        },
        {
          apiKey: 'Source',
          payload: { date: dayjs(payload?.reportDate).format('YYYY-MM-DD') },
          queryKey: 'ListTopContainmentSources',
        },
        {
          apiKey: 'ActionPath',
          payload: { initiativeType: 'DataCollision' },
          queryKey: 'GetActionPathInitiatives-DataCollision',
        },
        {
          apiKey: 'ActionPath',
          payload: { initiativeType: 'DOB' },
          queryKey: 'GetActionPathInitiatives-DOB',
        },
        {
          apiKey: 'ActionPath',
          payload: { initiativeType: 'SSN' },
          queryKey: 'GetActionPathInitiatives-SSN',
        },
        {
          apiKey: 'Event',
          payload: {
            endDate: dayjs(payload?.reportDate).format('YYYY-MM-DD'),
            eventTypeCodeList: payload?.eventTypeCodeList,
            page: 0,
            size: 25,
            sort: 'startDate,desc',
            startDate: dayjs(payload?.reportDate).subtract(6, 'months').format('YYYY-MM-DD'),
          },
          queryKey: 'ListEvents',
        },
      ];

      if (!isEqual(requests, theQueries)) {
        setTheQueries(requests);
      }
    },
    [theQueries]
  );

  return useMemo(() => {
    return { data: queries || undefined, handleSearch, isSearching };
  }, [isSearching, queries, handleSearch]);
};
